import type { Ton } from '../commun.js'
import { renommerSalles } from '../salles.js'
import { estVueDomaine, objet, type AppareilBref, type Collecteur, type Generique, type SalleDomaine, type Seuil, type SourceDomaine } from '../video/kit-modele.js'

/**
 * AUDIOVISUEL — modèle pur de `GET /v1/lighting` et `GET /v1/sound` (21/10/2026).
 *
 * Lumière : un port en DMX Hold est un MAINTIEN (la console n'envoie plus,
 * le nœud fonctionne et garde la dernière image), jamais un plantage.
 * Son : la jauge suit le LAeq 15 min, la projection et la limite ; le
 * message (« Limite LAeq 15 min atteinte dans 6 min (Salon)… ») est celui de
 * Sonde, jamais recalculé ici. Aucun conseil sur la console lumière.
 */

export interface PortDmx {
  port: string
  universe: number | null
  state: number | null
  state_label: string | null
  fps: number | null
  max_gap_ms: number | null
  gaps_5m: number | null
  sources: number | null
  priority: number | null
  hold: boolean | null
  at: string | null
  data_age_s: number | null
}

export interface NoeudLumiere { device: AppareilBref; ports: PortDmx[] }

export interface LightingDoc {
  generated_at: string
  collector: Collecteur
  sources: SourceDomaine[]
  zones: SalleDomaine[]
  counts: { ports: number; active: number; holds: number; gaps_5m: number | null }
  nodes: NoeudLumiere[]
  devices: AppareilBref[]
  generic: Generique[]
}

export function estLightingDoc(x: unknown): x is LightingDoc {
  return estVueDomaine(x, ['zones', 'nodes', 'devices', 'generic']) && objet(x['counts'])
}

export interface Sonometre {
  device: AppareilBref
  laeq_db: number | null
  lceq_db: number | null
  laeq15_db: number | null
  lceq15_db: number | null
  limit_a_db: number | null
  limit_c_db: number | null
  limit_source: 'setting' | 'device' | null
  proj_laeq15_db: number | null
  minutes_to_limit: number | null
  margin_db: number | null
  level: 'ok' | 'warning' | 'critical' | null
  message_fr: string | null
  at: string | null
  data_age_s: number | null
}

export interface SoundDoc {
  generated_at: string
  collector: Collecteur
  sources: SourceDomaine[]
  thresholds: Record<string, Seuil>
  horizon_min: number | null
  meters: Sonometre[]
  devices: AppareilBref[]
  generic: Generique[]
}

export function estSoundDoc(x: unknown): x is SoundDoc {
  return estVueDomaine(x, ['meters', 'devices', 'generic'])
}

/* ═══════════════ Lumière ═══════════════ */

/** Au-delà de 15 min (même règle que l'écran Réseau), une mesure est périmée : grise, jamais verte. */
export const MESURE_PERIMEE_S = 900
export const mesurePerimee = (age: number | null | undefined): boolean =>
  age === null || age === undefined || !Number.isFinite(age) || age > MESURE_PERIMEE_S

/**
 * État d'un port DMX en couleur. Le maintien (Hold) est ambre, jamais rose :
 * le nœud va bien. Une ligne que LumiNode ne relit plus est grise, quelle
 * que soit sa dernière valeur.
 */
export function etatPort(p: PortDmx): { ton: Ton; libelle: string } {
  if (mesurePerimee(p.data_age_s)) return { ton: 'gris', libelle: p.data_age_s === null ? 'Pas de mesure' : 'Donnée périmée' }
  if (p.hold === true || p.state === 3) return { ton: 'warn', libelle: 'Maintien (Hold)' }
  if (p.state === 1) return { ton: 'ok', libelle: p.state_label ?? 'Émission' }
  if (p.state === 2) return { ton: 'ok', libelle: p.state_label ?? 'Rétabli' }
  if (p.state === 0) return { ton: 'off', libelle: p.state_label ?? 'Inactif' }
  if (p.state === null && p.fps !== null) return p.fps > 0 ? { ton: 'ok', libelle: 'Émission' } : { ton: 'off', libelle: 'Inactif' }
  return { ton: 'gris', libelle: p.state_label ?? 'Inconnu' }
}

/** Ports d'un nœud en maintien, pour la phrase de tête. */
export function portsEnMaintien(n: NoeudLumiere): string[] {
  return n.ports.filter((p) => p.hold === true || p.state === 3).map((p) => p.port)
}

/** Mesures réseau de la lumière (MA-Net, Art-Net/sACN), une ligne par mesure connue. */
export function mesuresReseauLumiere(generic: Generique[]): Generique[] {
  const ordre = ['lumiere.universes_active', 'lumiere.manet_pps', 'lumiere.manet_pps_max', 'lumiere.manet_kbps', 'lumiere.apr_replies', 'lumiere.apr_reply_ratio', 'lumiere.apr_silence_s', 'lumiere.artpoll_interval_s', 'lumiere.prodjlink_packets']
  return generic.filter((g) => ordre.includes(g.metric)).sort((a, z) => ordre.indexOf(a.metric) - ordre.indexOf(z.metric) || a.entity_key.localeCompare(z.entity_key))
}

/* ═══════════════ Son ═══════════════ */

export const tonSonometre = (m: Sonometre): Ton => (mesurePerimee(m.data_age_s) ? 'gris'
  : m.level === 'critical' ? 'err' : m.level === 'warning' ? 'warn' : m.level === 'ok' ? 'ok' : 'gris')

export interface Jauge {
  min: number
  max: number
  /** Positions en % de la largeur ; null si la mesure manque. */
  actuel: number | null
  projete: number | null
  limite: number
}

/**
 * Jauge LAeq 15 min : de (limite − 15 dB) à (limite + 3 dB). Sans limite
 * connue, pas de jauge (on ne dessine pas une échelle inventée).
 */
export function jaugeSon(m: Sonometre): Jauge | null {
  if (m.limit_a_db === null) return null
  const min = m.limit_a_db - 15
  const max = m.limit_a_db + 3
  const pos = (v: number | null) => (v === null ? null : Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100)))
  return { min, max, actuel: pos(m.laeq15_db), projete: pos(m.proj_laeq15_db), limite: pos(m.limit_a_db)! }
}

/** Les messages de Sonde à mettre en tête (limite atteinte ou proche), les plus graves d'abord. */
export function alertesSon(meters: Sonometre[]): { uid: string; ton: Ton; texte: string }[] {
  const rang: Record<Ton, number> = { err: 0, warn: 1, ok: 2, off: 3, maint: 3, gris: 4 }
  return meters
    .filter((m) => m.message_fr && (m.level === 'warning' || m.level === 'critical' || (m.margin_db !== null && m.margin_db <= 0)))
    .map((m) => ({ uid: m.device.uid, ton: tonSonometre(m), texte: renommerSalles(m.message_fr) }))
    .sort((a, z) => rang[a.ton] - rang[z.ton])
}

/** Origine de la limite, en clair. */
export const origineLimite = (s: Sonometre['limit_source']): string =>
  s === 'setting' ? 'réglée dans Sonde' : s === 'device' ? 'lue sur le sonomètre' : 'non connue'
